import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Typography,
  useTheme,
} from "@mui/material";
import { useStateContext } from "contexts/ContextProvider";
import { useConnectivity } from "hooks/useConnectivity";
import {
  PING_INTERVAL_MS,
  getConnectivityState,
} from "services/connectivityMonitor";

/** Grace period before the overlay becomes visible. A reconnect episode that
 *  heals within this window (one dropped request) never flashes on screen —
 *  and the same flag decides whether the "Reconnected" snackbar is worth
 *  showing (only for episodes the user actually saw). */
const OVERLAY_FADE_DELAY_MS = 300;

/** How often the overlay refreshes its own "Attempt N" line while an outage
 *  is on screen. Kept local (instead of a global notification) so the rest of
 *  the app stays asleep during the outage. */
const LIVE_TICK_MS = 1000;

/**
 * App-wide full-screen overlay shown while the connectivity monitor
 * (`services/connectivityMonitor.js`) is diagnosing why the portal cannot
 * reach the server. Mounted ONCE by `NetworkErrorListener`, as a FIXED layer
 * above everything, so the page underneath — including any open form — stays
 * mounted and unsaved work survives.
 *
 * The heading explains the monitor's verdict:
 * - 'checking'    → "Reconnecting to the server…" (first diagnosis running)
 * - 'server-down' → X: the internet works, but the SERVER is not answering
 * - 'no-internet' → Y: the device itself cannot reach the internet
 *
 * Renders nothing while idle; on recovery it unmounts itself and shows a
 * one-shot "Reconnected" snackbar.
 */
const ServerReconnecting = () => {
  const theme = useTheme();
  const { showSnackbar } = useStateContext();
  const { status, isServerDown, isNoInternet, retryNow } = useConnectivity();

  const isIdle = status === "idle";

  const [fadeIn, setFadeIn] = useState(false);
  // Local copy of the monitor's live attempt counter. The monitor notifies
  // subscribers ONLY on verdict changes (routine ticks used to re-render the
  // whole app every 3s), so this overlay keeps its own view fresh with a
  // cheap local timer. Setting an unchanged value bails out of re-rendering,
  // so the 1s cadence costs nothing between real counter bumps.
  const [liveAttempt, setLiveAttempt] = useState(0);
  // True once the overlay has actually become VISIBLE (fade delay elapsed) —
  // lets the recovery snackbar fire exactly once per episode, and never for a
  // blip the user never saw.
  const wasShownRef = useRef(false);
  const prevStatusRef = useRef(status);

  // Fade in only after the grace delay; reset when the episode ends so the
  // next one starts hidden again. Until then the component renders NOTHING —
  // an invisible-but-mounted overlay must never swallow clicks.
  useEffect(() => {
    if (isIdle) {
      setFadeIn(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setFadeIn(true);
      wasShownRef.current = true;
    }, OVERLAY_FADE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isIdle]);

  // Live liveness line: poll the monitor's snapshot locally while an episode
  // is on screen. This is the ONLY thing that re-renders during an outage —
  // and only when `attempt` actually changes.
  useEffect(() => {
    if (isIdle) return undefined;

    setLiveAttempt(getConnectivityState().attempt);
    const timer = setInterval(() => {
      setLiveAttempt(getConnectivityState().attempt);
    }, LIVE_TICK_MS);

    return () => clearInterval(timer);
  }, [isIdle]);

  // Recovery: the monitor settled back to idle after the overlay had been
  // visible → confirm it to the user exactly once.
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;

    if (status === "idle" && prevStatus !== "idle" && wasShownRef.current) {
      wasShownRef.current = false;
      showSnackbar("Reconnected", theme, "success");
    }
  }, [status, showSnackbar, theme]);

  if (isIdle || !fadeIn) return null;

  // Heading / body / evidence per monitor verdict. Plain string variables
  // (rendered as expressions) keep the copy readable and free of JSX
  // apostrophe escaping.
  let heading = "Reconnecting to the server…";
  let message = "Checking the connection — one moment.";
  let diagnosis = null;

  if (isServerDown) {
    heading = "The server isn't responding";
    message =
      "The portal can reach the internet, but the server isn't answering right now. It may be restarting — we'll keep trying automatically.";
    diagnosis = "Server: unreachable · Internet: OK";
  } else if (isNoInternet) {
    heading = "You appear to be offline";
    message =
      "Your device can't reach the internet. Check your Wi-Fi or mobile data connection, then we'll reconnect automatically.";
    diagnosis = "Internet: unreachable";
  }

  // Liveness: the attempt counter ticks up on every completed failed check.
  const statusLine =
    liveAttempt > 0
      ? `Checking again in ${PING_INTERVAL_MS / 1000}s… · Attempt ${liveAttempt}`
      : "Checking the connection…";

  return (
    <Fade in appear timeout={400}>
      <Box
        role="alert"
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: theme.zIndex.modal + 100,
          backgroundColor: theme.palette.background.alt,
          px: { xs: 2, sm: 3 },
          py: { xs: 3, sm: 4 },
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 480, md: 520 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            backgroundColor: theme.palette.background.default,
            borderRadius: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
            boxShadow: 3,
            p: { xs: 3, sm: 4, md: 5 },
            // Auto margins center the card vertically AND horizontally, and
            // collapse gracefully when the card is taller than a short
            // viewport — so the top stays reachable via scroll instead of
            // being clipped by flex centering.
            m: "auto",
          }}
        >
          <Box
            component="img"
            src="/assets/ce-logo-full-h.svg"
            alt="CE Portal Logo"
            sx={{
              width: { xs: "160px", sm: "200px", md: "240px" },
              maxWidth: "100%",
              mb: { xs: 3, sm: 4 },
            }}
          />

          <CircularProgress
            size="4rem"
            sx={{
              color: theme.palette.primary.main,
              mb: { xs: 3, sm: 4 },
            }}
          />

          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1.35rem", sm: "1.6rem", md: "1.85rem" },
              color: theme.palette.text.primary,
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {heading}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "0.875rem", sm: "1rem" },
              maxWidth: 400,
              mb: diagnosis ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 2.5 },
            }}
          >
            {message}
          </Typography>

          {diagnosis && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 600,
                letterSpacing: "0.02em",
                mb: { xs: 2, sm: 2.5 },
              }}
            >
              {diagnosis}
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              mb: { xs: 3, sm: 4 },
            }}
          >
            {statusLine}
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={retryNow}
            sx={{
              borderRadius: "12px",
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.buttonHover,
              },
              px: { xs: 3, sm: 4 },
              py: { xs: 1, sm: 1.25 },
              fontSize: { xs: "1rem", sm: "1.125rem" },
              fontWeight: "bold",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Retry now
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default ServerReconnecting;
